import { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Calendar as CalendarIcon, Download, AlertTriangle, Users, List, Grid3x3, LayoutDashboard, Check, X, Ban, CheckCircle2, Trash2, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MONTHS_SHORT = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function dateStr(y, m, d) { return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`; }
function todayIso() { return new Date().toISOString().slice(0, 10); }

const LEGEND = [
  { color: "#16a34a", label: "Aprobadas" },
  { color: "#eab308", label: "Pendientes" },
  { color: "#fca5a5", label: "Rechazadas", muted: true },
  { color: "#94a3b8", label: "Canceladas", muted: true },
  { color: "#3b82f6", label: "Baja médica" },
  { color: "#a855f7", label: "Permiso retribuido" },
  { color: "#f97316", label: "Asuntos propios" },
];

// ═══ Selectores con valor "" = "todos" ═══
function NativeSelect({ value, onChange, options, placeholder, testid, className = "" }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className={`rounded-sm h-8 text-xs border border-slate-300 px-2 bg-white max-w-[180px] ${className}`}
            data-testid={testid}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function SearchableEmployeeSelect({ value, onChange, employees, testid }) {
  const [open, setOpen] = useState(false);
  const selected = employees.find((e) => e.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-sm h-8 text-xs border border-slate-300 px-2 bg-white flex items-center justify-between gap-2 min-w-[180px] max-w-[220px]"
          data-testid={testid}
        >
          <span className="truncate">{selected ? selected.label : "Todos los empleados"}</span>
          <ChevronsUpDown className="w-3 h-3 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[260px] rounded-sm" align="start">
        <Command>
          <CommandInput placeholder="Buscar empleado…" className="h-8 text-xs" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>Sin empleados para estos filtros</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => { onChange(""); setOpen(false); }} className="text-xs" data-testid={`${testid}-opt-all`}>
                <Check className={`w-3 h-3 mr-2 ${!value ? "opacity-100" : "opacity-0"}`} /> Todos los empleados
              </CommandItem>
              {employees.map((e) => (
                <CommandItem key={e.id} value={e.label.toLowerCase() + " " + e.id}
                             onSelect={() => { onChange(e.id); setOpen(false); }} className="text-xs"
                             data-testid={`${testid}-opt-${e.id}`}>
                  <Check className={`w-3 h-3 mr-2 ${value === e.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{e.label}</span>
                  <span className="ml-auto text-[9px] text-slate-400 truncate">{e.center || ""}{e.center && e.department ? " · " : ""}{e.department || ""}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const COVERAGE_COLORS = {
  ok: { bg: "#dcfce7", text: "#166534", barBg: "#16a34a" },
  ajustado: { bg: "#fef3c7", text: "#92400e", barBg: "#eab308" },
  critico: { bg: "#fee2e2", text: "#991b1b", barBg: "#dc2626" },
};



// ═══ Planning Mensual de Equipo (vista PRINCIPAL para RRHH — Iter 174) ═══
function TeamMonthlyPlanning({ year, month, items, onClickCell, sortBy, setSortBy, coverageDays, onClickCoverage, highlightDate }) {
  const total = daysInMonth(year, month);
  const today = new Date();
  const todayIsCurrent = today.getFullYear() === year && today.getMonth() === month;
  const todayDay = todayIsCurrent ? today.getDate() : -1;
  // Día seleccionado (desde modal de cobertura). Solo se aplica si pertenece al mes visible.
  let highlightDay = -1;
  if (highlightDate) {
    const [hy, hm, hd] = highlightDate.split("-").map(Number);
    if (hy === year && hm === month + 1) highlightDay = hd;
  }

  // Agrupar por employee + ordenar
  const byEmployee = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = it.employee_id;
      if (!map.has(key)) {
        map.set(key, { name: it.employee_name, center: it.center || "—", department: it.department || "—", items: [] });
      }
      map.get(key).items.push(it);
    }
    const arr = Array.from(map.entries());
    if (sortBy === "center") arr.sort((a, b) => (a[1].center || "").localeCompare(b[1].center || "") || (a[1].name || "").localeCompare(b[1].name || ""));
    else if (sortBy === "department") arr.sort((a, b) => (a[1].department || "").localeCompare(b[1].department || "") || (a[1].name || "").localeCompare(b[1].name || ""));
    else arr.sort((a, b) => (a[1].name || "").localeCompare(b[1].name || ""));
    return arr;
  }, [items, sortBy]);

  // Totales por día (footer)
  const totalsByDay = useMemo(() => {
    const counts = new Array(total + 1).fill(0);
    for (const [, info] of byEmployee) {
      for (const it of info.items) {
        if (it.status === "rejected" || it.status === "cancelled") continue;
        const s = new Date(it.start_date + "T00:00:00");
        const e = new Date(it.end_date + "T00:00:00");
        for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
          if (d.getFullYear() === year && d.getMonth() === month) counts[d.getDate()] += 1;
        }
      }
    }
    return counts;
  }, [byEmployee, year, month, total]);

  const peakCount = Math.max(...totalsByDay);

  return (
    <div className="overflow-x-auto border border-slate-200 rounded-sm bg-white" data-testid="team-monthly-planning">
      <div className="p-1.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
        <span className="font-semibold">{byEmployee.length} empleado(s) con ausencias en {MONTHS[month]} {year}</span>
        <span className="ml-auto">Ordenar:</span>
        <button className={`px-1.5 py-0.5 rounded-sm ${sortBy === "name" ? "bg-slate-900 text-white" : "hover:bg-slate-200"}`} onClick={() => setSortBy("name")} data-testid="sort-name">Nombre</button>
        <button className={`px-1.5 py-0.5 rounded-sm ${sortBy === "center" ? "bg-slate-900 text-white" : "hover:bg-slate-200"}`} onClick={() => setSortBy("center")} data-testid="sort-center">Centro</button>
        <button className={`px-1.5 py-0.5 rounded-sm ${sortBy === "department" ? "bg-slate-900 text-white" : "hover:bg-slate-200"}`} onClick={() => setSortBy("department")} data-testid="sort-dept">Departamento</button>
      </div>
      <table className="text-[11px] border-collapse w-full">
        <thead className="bg-slate-50 sticky top-0 z-20">
          <tr>
            <th className="text-left p-2 border-b border-slate-200 min-w-[220px] sticky left-0 bg-slate-50 z-30">Empleado</th>
            {Array.from({ length: total }).map((_, d) => {
              const day = d + 1;
              const date = new Date(year, month, day);
              const dow = date.getDay();
              const isWeekend = dow === 0 || dow === 6;
              const isToday = day === todayDay;
              const isHighlight = day === highlightDay;
              return (
                <th key={d} className={`text-center border-b ${isHighlight ? "border-b-2 border-blue-500 bg-blue-50 text-blue-900 font-bold" : "border-slate-200"} ${isWeekend && !isHighlight ? "bg-slate-100" : ""} ${isToday && !isHighlight ? "bg-amber-100 text-amber-900 font-bold" : ""}`} style={{ minWidth: "28px", padding: "4px 0" }}>
                  <div className="text-[10px]">{["D","L","M","X","J","V","S"][dow]}</div>
                  <div className="font-mono">{day}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody data-testid="team-tbody">
          {byEmployee.length === 0 && (
            <tr><td colSpan={total + 1} className="text-center p-6 text-slate-400 text-xs">Sin ausencias en {MONTHS[month]} {year}</td></tr>
          )}
          {byEmployee.map(([empId, info]) => {
            const itemsByDay = {};
            for (const it of info.items) {
              const s = new Date(it.start_date + "T00:00:00");
              const e = new Date(it.end_date + "T00:00:00");
              for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
                if (d.getFullYear() === year && d.getMonth() === month) itemsByDay[d.getDate()] = it;
              }
            }
            return (
              <tr key={empId} className="border-b border-slate-100 hover:bg-slate-50/40">
                <td className="p-1.5 sticky left-0 bg-white border-r border-slate-200" style={{ minWidth: "220px" }}>
                  <div className="font-semibold text-slate-800 text-[12px] leading-tight">{info.name}</div>
                  <div className="text-[10px] text-slate-400">{info.center}{info.department && info.department !== "—" ? ` · ${info.department}` : ""}</div>
                </td>
                {Array.from({ length: total }).map((_, d) => {
                  const day = d + 1;
                  const ev = itemsByDay[day];
                  const date = new Date(year, month, day);
                  const dow = date.getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  const isToday = day === todayDay;
                  const isHighlight = day === highlightDay;
                  const muted = ev && (ev.status === "rejected" || ev.status === "cancelled");
                  // Iter 188 (P0) — Pendientes con patrón rayado para diferenciar visualmente
                  const isPending = ev && ev.status === "pending";
                  return (
                    <td key={d} className={`p-0 border-r ${isHighlight ? "border-l-2 border-r-2 border-blue-500 bg-blue-50/30" : "border-slate-50"} ${isWeekend && !isHighlight ? "bg-slate-50/70" : ""} ${isToday && !isHighlight ? "ring-1 ring-amber-300 ring-inset" : ""}`}>
                      <button
                        className="w-full hover:ring-1 hover:ring-slate-900 hover:ring-inset transition-shadow"
                        style={{
                          background: ev ? (isPending
                            ? `repeating-linear-gradient(45deg, ${ev.color} 0 4px, ${ev.color}55 4px 8px)`
                            : ev.color) : "transparent",
                          opacity: muted ? 0.35 : 1,
                          height: "26px",
                          cursor: ev ? "pointer" : "default",
                        }}
                        onClick={() => ev && onClickCell([ev])}
                        title={ev ? `${ev.employee_name} · ${ev.type_label} · ${ev.status_label}${isPending ? " (PENDIENTE)" : ""}\n${ev.start_date} → ${ev.end_date}` : ""}
                        data-testid={ev ? `team-cell-${empId}-${day}` : undefined}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        {byEmployee.length > 0 && (
          <tfoot className="bg-slate-100 sticky bottom-0">
            <tr>
              <td className="p-1.5 sticky left-0 bg-slate-100 text-[10px] uppercase tracking-wider font-semibold text-slate-600 border-t-2 border-slate-300">
                Total ausentes
              </td>
              {Array.from({ length: total }).map((_, d) => {
                const day = d + 1;
                const n = totalsByDay[day];
                const isPeak = peakCount > 0 && n === peakCount;
                const isHighlight = day === highlightDay;
                return (
                  <td key={d} className={`text-center border-t-2 border-slate-300 text-[10px] font-mono ${isHighlight ? "border-l-2 border-r-2 border-blue-500 bg-blue-50" : ""} ${isPeak && !isHighlight ? "bg-amber-200 text-amber-900 font-bold" : ""} ${day === todayDay && !isHighlight ? "bg-amber-100" : ""}`}>
                    {n > 0 ? n : ""}
                  </td>
                );
              })}
            </tr>
            {coverageDays && coverageDays.length > 0 && (
              <tr data-testid="coverage-row">
                <td className="p-1.5 sticky left-0 bg-slate-100 text-[10px] uppercase tracking-wider font-semibold text-slate-600 border-t border-slate-200">
                  Cobertura
                </td>
                {Array.from({ length: total }).map((_, d) => {
                  const day = d + 1;
                  const cov = coverageDays.find((c) => c.day === day);
                  const isHighlight = day === highlightDay;
                  if (!cov || cov.total === 0) return <td key={d} className={`text-center text-[10px] text-slate-300 ${isHighlight ? "border-l-2 border-r-2 border-blue-500 bg-blue-50" : ""}`}>—</td>;
                  const c = COVERAGE_COLORS[cov.status] || COVERAGE_COLORS.ok;
                  return (
                    <td key={d} className={`p-0 border-r border-slate-50 ${isHighlight ? "border-l-2 border-r-2 border-blue-500" : ""}`}>
                      <button
                        className="w-full h-6 text-[9px] font-mono font-bold hover:ring-1 hover:ring-slate-900 hover:ring-inset"
                        style={{ background: c.bg, color: c.text }}
                        onClick={() => onClickCoverage && onClickCoverage(cov)}
                        title={`${cov.available}/${cov.total} disponibles (${cov.pct_available}%) · ${cov.status}`}
                        data-testid={`cov-cell-${day}`}
                      >
                        {cov.available}/{cov.total}
                      </button>
                    </td>
                  );
                })}
              </tr>
            )}
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ═══ Vista MENSUAL del empleado (calendario tipo agenda) ═══
function EmployeeMonthlyView({ year, month, items, onClickDay }) {
  const total = daysInMonth(year, month);
  const firstDow = new Date(year, month, 1).getDay() || 7;
  const cells = [];
  for (let i = 1; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  const today = new Date();
  const todayDay = (today.getFullYear() === year && today.getMonth() === month) ? today.getDate() : -1;

  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!it.start_date || !it.end_date) continue;
      const s = new Date(it.start_date + "T00:00:00");
      const e = new Date(it.end_date + "T00:00:00");
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === year && d.getMonth() === month) {
          const k = d.getDate();
          (map[k] = map[k] || []).push(it);
        }
      }
    }
    return map;
  }, [items, year, month]);

  return (
    <div className="border border-slate-200 rounded-sm bg-white">
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
        {["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"].map((d, i) => <div key={i} className="text-center py-1.5">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="aspect-square border-r border-b border-slate-100" />;
          const evs = byDay[d] || [];
          const isToday = d === todayDay;
          return (
            <button
              key={i}
              onClick={() => evs.length && onClickDay(evs)}
              className={`aspect-square border-r border-b border-slate-100 hover:bg-slate-50 relative p-1 text-left flex flex-col justify-between ${isToday ? "bg-amber-50 ring-1 ring-amber-300 ring-inset" : ""}`}
              data-testid={`emp-day-${month}-${d}`}
            >
              <span className={`text-xs font-mono ${isToday ? "text-amber-900 font-bold" : "text-slate-700"}`}>{d}</span>
              {evs.slice(0, 2).map((e, idx) => (
                <span key={idx} className="text-[9px] text-white rounded-sm px-1 py-0.5 truncate" style={{ background: e.color }}>
                  {e.type_label.split(" ")[0]}
                </span>
              ))}
              {evs.length > 2 && <span className="text-[9px] text-slate-400">+{evs.length - 2}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═══ Vista Año compacta ═══
function YearDots({ year, items, onClickDay }) {
  const byDate = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!it.start_date || !it.end_date) continue;
      const s = new Date(it.start_date + "T00:00:00");
      const e = new Date(it.end_date + "T00:00:00");
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const k = dateStr(d.getFullYear(), d.getMonth(), d.getDate());
        (map[k] = map[k] || []).push(it);
      }
    }
    return map;
  }, [items]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {MONTHS_SHORT.map((mn, m) => {
        const total = daysInMonth(year, m);
        return (
          <div key={m} className="border border-slate-200 rounded-sm p-2 bg-white">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{mn}</p>
            <div className="grid grid-cols-7 gap-0.5">
              {Array.from({ length: total }).map((_, d) => {
                const k = dateStr(year, m, d + 1);
                const evs = byDate[k] || [];
                const top = evs[0];
                return (
                  <button
                    key={d}
                    onClick={() => evs.length && onClickDay(evs)}
                    className="aspect-square text-[8px] rounded-sm flex items-center justify-center hover:ring-1 hover:ring-slate-400"
                    style={{ background: top ? top.color : "transparent" }}
                    title={evs.length ? `${evs.length} ausencia(s)` : ""}
                  >
                    {evs.length > 1 ? <span className="text-white font-bold">{evs.length}</span>
                                    : <span className="text-slate-400">{d + 1}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══ Vista Lista ═══
function ListView({ items, onClickItem }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-sm bg-white">
      <table className="w-full text-xs">
        <thead className="bg-slate-50">
          <tr className="text-[10px] uppercase tracking-wider text-slate-500">
            <th className="text-left p-2">Empleado</th><th className="text-left p-2">Centro</th><th className="text-left p-2">Dept.</th>
            <th className="text-left p-2">Tipo</th><th className="text-left p-2">Inicio</th><th className="text-left p-2">Fin</th>
            <th className="text-right p-2">Días</th><th className="text-left p-2">Estado</th><th className="text-left p-2">Aprobador</th>
          </tr>
        </thead>
        <tbody data-testid="list-tbody">
          {items.length === 0 && <tr><td colSpan="9" className="text-center p-4 text-slate-400">Sin solicitudes</td></tr>}
          {items.map((it) => (
            <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onClickItem([it])} data-testid={`list-row-${it.id}`}>
              <td className="p-2 font-semibold">{it.employee_name}</td>
              <td className="p-2">{it.center || "—"}</td>
              <td className="p-2">{it.department || "—"}</td>
              <td className="p-2"><span className="text-[10px] uppercase px-1.5 py-0.5 rounded-sm" style={{ background: it.color, color: "#fff" }}>{it.type_label}</span></td>
              <td className="p-2 font-mono">{it.start_date}</td>
              <td className="p-2 font-mono">{it.end_date}</td>
              <td className="p-2 text-right font-mono">{it.working_days}</td>
              <td className="p-2">{it.status_label}</td>
              <td className="p-2 text-[10px] text-slate-500">{it.approver_name || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══ Dashboard RRHH (Iter 176) ═══
function HrAbsencesDashboard({ axiosAuth, onClickRequest, onOpenPlanning }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCov, setOpenCov] = useState(null);  // { scope:'centro'|'departamento', entry }
  // Iter 192 — Periodo (today/week/fortnight/month) para la vista de Resumen por periodo
  const [period, setPeriod] = useState("week");
  const [periodData, setPeriodData] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosAuth().get("/admin/hr-absences-dashboard");
        if (alive) setData(r.data);
      } catch {
        if (alive) toast.error("Error cargando dashboard RRHH");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [axiosAuth]);

  // Iter 192 — Cargar disponibilidad por periodo cuando cambia
  useEffect(() => {
    let alive = true;
    setPeriodLoading(true);
    (async () => {
      try {
        const r = await axiosAuth().get(`/admin/hr-absences-availability-by-period?period=${period}`);
        if (alive) setPeriodData(r.data);
      } catch {
        if (alive) toast.error("Error cargando disponibilidad por periodo");
      } finally { if (alive) setPeriodLoading(false); }
    })();
    return () => { alive = false; };
  }, [axiosAuth, period]);

  if (loading || !data) return <div className="p-6 text-xs text-slate-500">Cargando indicadores…</div>;
  const c = data.counters;

  return (
    <div className="space-y-3" data-testid="hr-absences-dashboard">
      {/* KPIs principales */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Card className="rounded-sm p-3 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Ausentes hoy</p>
          <p className="text-3xl font-mono font-bold text-slate-900" data-testid="kpi-today">{c.absent_today}</p>
          <p className="text-[10px] text-slate-400">de {c.total_employees} empleados</p>
        </Card>
        <Card className="rounded-sm p-3 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Ausentes mañana</p>
          <p className="text-3xl font-mono font-bold text-slate-900" data-testid="kpi-tomorrow">{c.absent_tomorrow}</p>
        </Card>
        <Card className="rounded-sm p-3 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Próximos 30 días</p>
          <p className="text-3xl font-mono font-bold text-slate-900" data-testid="kpi-30d">{c.active_next_30d}</p>
          <p className="text-[10px] text-slate-400">solicitudes activas</p>
        </Card>
        <Card className={`rounded-sm p-3 border ${c.critical_overlaps > 0 ? "border-amber-300 bg-amber-50/60" : "border-slate-200"}`}>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Solapes críticos</p>
          <p className={`text-3xl font-mono font-bold ${c.critical_overlaps > 0 ? "text-amber-700" : "text-slate-900"}`} data-testid="kpi-overlaps">{c.critical_overlaps}</p>
        </Card>
        <Card className={`rounded-sm p-3 border ${c.alerts > 0 ? "border-red-300 bg-red-50/60" : "border-slate-200"}`}>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Alertas activas</p>
          <p className={`text-3xl font-mono font-bold ${c.alerts > 0 ? "text-red-700" : "text-slate-900"}`} data-testid="kpi-alerts">{c.alerts}</p>
        </Card>
        <Card className={`rounded-sm p-3 border ${c.critical_days_this_month > 0 ? "border-red-300 bg-red-50/60" : "border-slate-200"}`}>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Días críticos del mes</p>
          <p className={`text-3xl font-mono font-bold ${c.critical_days_this_month > 0 ? "text-red-700" : "text-slate-900"}`} data-testid="kpi-critical-days">{c.critical_days_this_month}</p>
        </Card>
      </div>

      {/* Línea de KPIs secundarios */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Card className="rounded-sm p-2.5 border border-slate-200" data-testid="kpi-lowest-day">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Día con menor cobertura</p>
          {data.lowest_coverage_day ? (
            <p className="text-xs mt-0.5"><span className="font-mono font-bold">{data.lowest_coverage_day.date}</span> · {data.lowest_coverage_day.available}/{data.lowest_coverage_day.total} disponibles ({data.lowest_coverage_day.pct_available}%)</p>
          ) : <p className="text-xs text-slate-400">—</p>}
        </Card>
        <Card className="rounded-sm p-2.5 border border-slate-200" data-testid="kpi-most-tensioned">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Más tensionado hoy</p>
          {data.most_tensioned ? (
            <p className="text-xs mt-0.5"><span className="uppercase text-[9px] bg-slate-200 px-1 rounded-sm mr-1">{data.most_tensioned.scope}</span><strong>{data.most_tensioned.name}</strong>: {data.most_tensioned.absent_today}/{data.most_tensioned.total} ausentes ({data.most_tensioned.pct_absent_today}%)</p>
          ) : <p className="text-xs text-slate-400">Sin tensión detectada</p>}
        </Card>
        <Card className="rounded-sm p-2.5 border border-slate-200" data-testid="kpi-top5">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Top 5 días con más ausencias (30d)</p>
          <div className="flex gap-1 flex-wrap mt-0.5">
            {(data.top5_peak_days || []).slice(0, 5).map((p) => (
              <span key={p.date} className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded-sm font-mono">{p.date} · <strong>{p.count}</strong></span>
            ))}
            {(!data.top5_peak_days || data.top5_peak_days.length === 0) && <span className="text-xs text-slate-400">—</span>}
          </div>
        </Card>
      </div>

      {/* Próximos días con cobertura crítica */}
      {data.upcoming_critical && data.upcoming_critical.length > 0 && (
        <Card className="rounded-sm p-3 border border-red-200 bg-red-50/40" data-testid="upcoming-critical">
          <p className="text-[10px] uppercase tracking-wider text-red-800 font-semibold mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Próximos días con cobertura crítica
          </p>
          <div className="flex gap-1 flex-wrap">
            {data.upcoming_critical.map((d) => (
              <span key={d.date} className="text-[11px] px-2 py-0.5 bg-white border border-red-300 rounded-sm font-mono text-red-900">
                {d.date}: {d.available}/{d.total} ({d.pct_available}%)
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Alertas de cobertura */}
      {data.alerts.length > 0 && (
        <Card className="rounded-sm p-3 border border-red-200 bg-red-50/40" data-testid="alerts-panel">
          <p className="text-[10px] uppercase tracking-wider text-red-800 font-semibold mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Alertas de falta de personal
          </p>
          <ul className="space-y-1 text-xs">
            {data.alerts.map((a, i) => (
              <li key={i} className={`px-2 py-1 rounded-sm ${a.level === "critical" ? "bg-red-100 text-red-900" : "bg-amber-100 text-amber-900"}`}>
                <strong className="uppercase text-[9px] mr-1">{a.level}</strong> {a.msg}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Ausentes hoy */}
        <Card className="rounded-sm p-3 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Ausentes hoy ({data.today})</p>
          {data.absent_today_list.length === 0 ? (
            <p className="text-xs text-slate-400">Sin ausencias hoy. ✓</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {data.absent_today_list.map((it) => (
                <button key={it.id} onClick={() => onClickRequest([it])}
                        className="w-full text-left text-xs border-l-4 bg-slate-50 hover:bg-slate-100 p-2 rounded-sm"
                        style={{ borderColor: it.color }}>
                  <div className="font-semibold">{it.employee_name}</div>
                  <div className="text-[10px] text-slate-500">{it.type_label} · {it.center || "Sin centro"} · {it.start_date} → {it.end_date}</div>
                </button>
              ))}
            </div>
          )}
        </Card>
        {/* Ausentes mañana */}
        <Card className="rounded-sm p-3 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Ausentes mañana ({data.tomorrow})</p>
          {data.absent_tomorrow_list.length === 0 ? (
            <p className="text-xs text-slate-400">Sin ausencias mañana. ✓</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {data.absent_tomorrow_list.map((it) => (
                <button key={it.id} onClick={() => onClickRequest([it])}
                        className="w-full text-left text-xs border-l-4 bg-slate-50 hover:bg-slate-100 p-2 rounded-sm"
                        style={{ borderColor: it.color }}>
                  <div className="font-semibold">{it.employee_name}</div>
                  <div className="text-[10px] text-slate-500">{it.type_label} · {it.center || "Sin centro"} · {it.start_date} → {it.end_date}</div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Disponibilidad hoy por OFICIO (departamento) — ordenado de menor a mayor cobertura */}
        <Card className="rounded-sm p-3 border border-slate-200" data-testid="availability-by-dept">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Disponibilidad hoy por oficio
            <span className="ml-2 text-[9px] normal-case text-slate-400">(ordenado de menor a mayor cobertura)</span>
          </p>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {data.coverage_depts.map((dd) => {
              const col = COVERAGE_COLORS[dd.status] || COVERAGE_COLORS.ok;
              return (
                <button
                  key={dd.name}
                  onClick={() => setOpenCov({ scope: "departamento", entry: dd })}
                  className="w-full text-left text-xs border border-slate-200 hover:border-slate-400 rounded-sm p-2 transition-colors"
                  data-testid={`dept-row-${dd.name.replace(/\s+/g, "_")}`}
                >
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: col.barBg }} />
                      <span className="font-semibold truncate">{dd.name}</span>
                    </div>
                    <span className="text-[11px] font-mono shrink-0">
                      <strong>{dd.available} de {dd.total}</strong> disponibles
                    </span>
                    <span className="text-[10px] font-mono shrink-0 px-1.5 py-0.5 rounded-sm" style={{ background: col.bg, color: col.text }}>
                      {dd.pct_available}%
                    </span>
                  </div>
                  <div className="h-1 bg-slate-100 rounded-sm overflow-hidden mt-1">
                    <div className="h-full" style={{ width: `${Math.min(100, dd.pct_available)}%`, background: col.barBg }} />
                  </div>
                  {dd.absent_today > 0 && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      {dd.absent_today} ausente{dd.absent_today !== 1 ? "s" : ""}: {dd.names_absent_today.slice(0, 4).join(", ")}{dd.names_absent_today.length > 4 ? ` +${dd.names_absent_today.length - 4}` : ""}
                    </div>
                  )}
                </button>
              );
            })}
            {data.coverage_depts.length === 0 && <p className="text-xs text-slate-400">Sin departamentos configurados</p>}
          </div>
        </Card>
        {/* Cobertura por CENTRO — misma logica, ordenado por menor a mayor */}
        <Card className="rounded-sm p-3 border border-slate-200" data-testid="availability-by-center">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Disponibilidad hoy por centro
            <span className="ml-2 text-[9px] normal-case text-slate-400">(ordenado de menor a mayor)</span>
          </p>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {data.coverage_centers.map((cc) => {
              const col = COVERAGE_COLORS[cc.status] || COVERAGE_COLORS.ok;
              return (
                <button
                  key={cc.name}
                  onClick={() => setOpenCov({ scope: "centro", entry: cc })}
                  className="w-full text-left text-xs border border-slate-200 hover:border-slate-400 rounded-sm p-2 transition-colors"
                  data-testid={`center-row-${cc.name.replace(/\s+/g, "_")}`}
                >
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: col.barBg }} />
                      <span className="font-semibold truncate">{cc.name}</span>
                    </div>
                    <span className="text-[11px] font-mono shrink-0">
                      <strong>{cc.available} de {cc.total}</strong> disponibles
                    </span>
                    <span className="text-[10px] font-mono shrink-0 px-1.5 py-0.5 rounded-sm" style={{ background: col.bg, color: col.text }}>
                      {cc.pct_available}%
                    </span>
                  </div>
                  <div className="h-1 bg-slate-100 rounded-sm overflow-hidden mt-1">
                    <div className="h-full" style={{ width: `${Math.min(100, cc.pct_available)}%`, background: col.barBg }} />
                  </div>
                  {cc.absent_today > 0 && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      {cc.absent_today} ausente{cc.absent_today !== 1 ? "s" : ""}: {cc.names_absent_today.slice(0, 4).join(", ")}{cc.names_absent_today.length > 4 ? ` +${cc.names_absent_today.length - 4}` : ""}
                    </div>
                  )}
                </button>
              );
            })}
            {data.coverage_centers.length === 0 && <p className="text-xs text-slate-400">Sin centros configurados</p>}
          </div>
        </Card>
      </div>

      {/* Top días con más ausencias */}
      {data.peak_days.length > 0 && (
        <Card className="rounded-sm p-3 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Días con más ausencias (próximos 30 días)</p>
          <div className="flex gap-1 flex-wrap">
            {data.peak_days.slice(0, 10).map((p) => (
              <span key={p.date} className="text-xs px-2 py-1 bg-slate-100 rounded-sm font-mono">
                {p.date} · <strong>{p.count}</strong>
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Solapes */}
      {data.overlaps.length > 0 && (
        <Card className="rounded-sm p-3 border border-amber-300 bg-amber-50/40">
          <p className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-2">⚠ Solapes detectados ({data.overlaps.length})</p>
          <div className="space-y-1 max-h-48 overflow-y-auto text-xs">
            {data.overlaps.map((o, i) => (
              <div key={i} className="border-l-4 border-amber-400 bg-white px-2 py-1">
                <strong>{o.unit}</strong>: {o.a_name} ⇆ {o.b_name} · {o.start} → {o.end}
              </div>
            ))}
          </div>
        </Card>
      )}


      {/* ─── Iter 192 — Resumen por PERIODO (semana/quincena/mes) ─────── */}
      <Card className="rounded-sm p-3 border-2 border-slate-300 bg-slate-50/40" data-testid="period-summary-block">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-700 font-bold">Resumen por periodo</p>
            <p className="text-[10px] text-slate-500">Disponibilidad agregada con peor día y empleados ausentes en el rango.</p>
          </div>
          <div className="flex items-center gap-2" data-testid="period-selector">
            {[
              {value:"today", label:"Hoy"},
              {value:"week", label:"Semana"},
              {value:"fortnight", label:"Quincena"},
              {value:"month", label:"Mes"},
            ].map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                data-testid={`period-${p.value}`}
                className={`text-[11px] uppercase tracking-wider font-semibold rounded-sm px-3 py-1 border ${period === p.value ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
              >{p.label}</button>
            ))}
            <Button
              size="sm"
              onClick={async () => {
                try {
                  const r = await axiosAuth().get(`/admin/hr-absences-availability-by-period.xlsx?period=${period}`, { responseType: "blob" });
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(r.data);
                  link.download = `planificacion-ausencias-${({today:"hoy",week:"semana",fortnight:"quincena",month:"mes"}[period])}-${(periodData?.start_date) || new Date().toISOString().slice(0,10)}.xlsx`;
                  link.click(); URL.revokeObjectURL(link.href);
                  toast.success("Excel del periodo descargado");
                } catch { toast.error("Error descargando Excel del periodo"); }
              }}
              className="rounded-sm text-[11px] uppercase font-semibold ml-1"
              data-testid="period-export-xlsx"
            ><Download className="w-3 h-3 mr-1" /> Excel del periodo</Button>
          </div>
        </div>

        {periodLoading || !periodData ? (
          <p className="text-xs text-slate-400">Cargando…</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-white border border-slate-200 rounded-sm p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Rango</p>
                <p className="text-[11px] font-mono font-bold text-slate-900" data-testid="period-range">{periodData.start_date} → {periodData.end_date} <span className="text-slate-400">({periodData.days_count}d)</span></p>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Ausentes en el periodo</p>
                <p className="text-xl font-mono font-bold text-slate-900" data-testid="period-absent-total">{periodData.counters?.absent_in_period}</p>
              </div>
              <div className={`border rounded-sm p-2 ${periodData.counters?.critical_days > 0 ? "border-red-300 bg-red-50/60" : "bg-white border-slate-200"}`}>
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Días críticos</p>
                <p className={`text-xl font-mono font-bold ${periodData.counters?.critical_days > 0 ? "text-red-700" : "text-slate-900"}`} data-testid="period-critical-days">{periodData.counters?.critical_days}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Día con menor cobertura</p>
                {periodData.counters?.lowest_day ? (
                  <p className="text-[11px] font-mono font-bold text-slate-900" data-testid="period-lowest-day">{periodData.counters.lowest_day} · {periodData.counters.lowest_pct}%</p>
                ) : <p className="text-xs text-slate-400">—</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Disponibilidad por OFICIO en el periodo */}
              <div data-testid="period-availability-by-department">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  Disponibilidad {{today:"hoy", week:"semana", fortnight:"quincena", month:"mes"}[period]} por oficio
                </p>
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {(periodData.availability_by_department || []).map((g) => {
                    const col = COVERAGE_COLORS[g.status] || COVERAGE_COLORS.ok;
                    return (
                      <div key={g.name} className="bg-white border border-slate-200 rounded-sm p-2 text-xs" data-testid={`period-dept-${g.name.replace(/\s+/g,"_")}`}>
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="w-2 h-2 rounded-sm shrink-0" style={{background: col.barBg}} />
                            <span className="font-semibold truncate">{g.name}</span>
                          </div>
                          <span className="text-[10px] font-mono shrink-0 text-slate-700">
                            <strong>{g.avg_available}</strong> de {g.total} disponibles (media)
                          </span>
                          <span className="text-[10px] font-mono shrink-0 px-1.5 py-0.5 rounded-sm" style={{background: col.bg, color: col.text}}>
                            min {g.min_pct_available}%
                          </span>
                        </div>
                        {g.worst_day && (
                          <div className="text-[10px] text-slate-600 mt-1">
                            Peor día: <span className="font-mono">{g.worst_day.date}</span>, {g.worst_day.available}/{g.worst_day.total} disponibles
                            {g.critical_days_count > 0 && <span className="ml-2 text-red-700 font-semibold">· {g.critical_days_count} día{g.critical_days_count!==1?"s":""} crítico{g.critical_days_count!==1?"s":""}</span>}
                          </div>
                        )}
                        {(g.absences || []).length > 0 && (
                          <div className="text-[10px] text-slate-500 mt-1">
                            Ausentes: {g.absences.slice(0, 4).map(a => `${a.employee_name} (${a.start_date.slice(5)}→${a.end_date.slice(5)})`).join(", ")}
                            {g.absences.length > 4 ? ` +${g.absences.length - 4}` : ""}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(periodData.availability_by_department || []).length === 0 && <p className="text-xs text-slate-400">Sin departamentos.</p>}
                </div>
              </div>

              {/* Disponibilidad por CENTRO en el periodo */}
              <div data-testid="period-availability-by-center">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  Disponibilidad {{today:"hoy", week:"semana", fortnight:"quincena", month:"mes"}[period]} por centro
                </p>
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {(periodData.availability_by_center || []).map((g) => {
                    const col = COVERAGE_COLORS[g.status] || COVERAGE_COLORS.ok;
                    return (
                      <div key={g.name} className="bg-white border border-slate-200 rounded-sm p-2 text-xs" data-testid={`period-center-${g.name.replace(/\s+/g,"_")}`}>
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="w-2 h-2 rounded-sm shrink-0" style={{background: col.barBg}} />
                            <span className="font-semibold truncate">{g.name}</span>
                          </div>
                          <span className="text-[10px] font-mono shrink-0 text-slate-700">
                            <strong>{g.avg_available}</strong> de {g.total} disponibles (media)
                          </span>
                          <span className="text-[10px] font-mono shrink-0 px-1.5 py-0.5 rounded-sm" style={{background: col.bg, color: col.text}}>
                            min {g.min_pct_available}%
                          </span>
                        </div>
                        {g.worst_day && (
                          <div className="text-[10px] text-slate-600 mt-1">
                            Peor día: <span className="font-mono">{g.worst_day.date}</span>, {g.worst_day.available}/{g.worst_day.total} disponibles
                            {g.critical_days_count > 0 && <span className="ml-2 text-red-700 font-semibold">· {g.critical_days_count} día{g.critical_days_count!==1?"s":""} crítico{g.critical_days_count!==1?"s":""}</span>}
                          </div>
                        )}
                        {(g.absences || []).length > 0 && (
                          <div className="text-[10px] text-slate-500 mt-1">
                            Ausentes: {g.absences.slice(0, 4).map(a => `${a.employee_name} (${a.start_date.slice(5)}→${a.end_date.slice(5)})`).join(", ")}
                            {g.absences.length > 4 ? ` +${g.absences.length - 4}` : ""}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(periodData.availability_by_center || []).length === 0 && <p className="text-xs text-slate-400">Sin centros.</p>}
                </div>
              </div>
            </div>

            {/* Días críticos del periodo */}
            {(periodData.critical_days || []).length > 0 && (
              <div className="bg-red-50/40 border border-red-200 rounded-sm p-2" data-testid="period-critical-days-block">
                <p className="text-[10px] uppercase tracking-wider text-red-800 font-semibold mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Días críticos del periodo ({periodData.critical_days.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {periodData.critical_days.map(d => (
                    <span key={d.date} className="text-[10px] px-2 py-0.5 bg-white border border-red-300 rounded-sm font-mono text-red-900">
                      {d.date}: {d.available}/{d.total} ({d.pct_available}%)
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>


      {/* Modal detalle de cobertura por oficio/centro HOY */}
      <Dialog open={!!openCov} onOpenChange={(o) => !o && setOpenCov(null)}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="coverage-unit-modal">
          {openCov && (() => {
            const e = openCov.entry;
            const col = COVERAGE_COLORS[e.status] || COVERAGE_COLORS.ok;
            const statusLabel = { ok: "OK", ajustado: "Ajustado", critico: "Crítico" }[e.status] || e.status;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-base uppercase">{openCov.scope === "centro" ? "Centro" : "Oficio / departamento"} · {e.name}</DialogTitle>
                  <DialogDescription className="text-xs">
                    <span className="px-2 py-0.5 rounded-sm font-mono mr-2" style={{ background: col.bg, color: col.text }}>{statusLabel}</span>
                    <strong className="font-mono">{e.available} de {e.total}</strong> disponibles · {e.pct_available}% · {e.absent_today} ausente(s) hoy
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
                  <Card className="rounded-sm border border-slate-200 p-2">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Disponibles ({e.available})</p>
                    {e.available_today_list.length === 0 ? (
                      <p className="text-xs text-slate-400">Nadie disponible</p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {e.available_today_list.map((emp) => (
                          <li key={emp.id} className="border-l-2 border-emerald-500 pl-2 py-0.5">
                            <strong>{emp.name}</strong>
                            {(emp.center || emp.department) && (
                              <div className="text-[10px] text-slate-400">{emp.center}{emp.center && emp.department ? " · " : ""}{emp.department}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                  <Card className="rounded-sm border border-slate-200 p-2">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Ausentes hoy ({e.absent_today})</p>
                    {e.absent_today_items.length === 0 ? (
                      <p className="text-xs text-slate-400">Sin ausencias hoy</p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {e.absent_today_items.map((a) => (
                          <li key={a.id} className="border-l-4 pl-2 py-0.5" style={{ borderColor: a.color }}>
                            <div className="font-semibold">{a.employee_name}</div>
                            <div className="text-[10px] text-slate-500">
                              <span className="px-1 rounded-sm text-white" style={{ background: a.color }}>{a.type_label}</span>
                              <span className="ml-1">{a.status_label}</span>
                              <span className="ml-1 text-slate-400">· {a.start_date}→{a.end_date}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                </div>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => setOpenCov(null)} className="rounded-sm text-xs">Cerrar</Button>
                  {onOpenPlanning && (
                    <Button size="sm" className="rounded-sm text-xs" data-testid="btn-view-planning"
                            onClick={() => {
                              onOpenPlanning({
                                scope: openCov.scope,
                                name: openCov.entry.name,
                              });
                              setOpenCov(null);
                            }}>
                      <Users className="w-3 h-3 mr-1" /> Ver planning del día
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══ Modal de acciones rápidas (Iter 175) ═══
function ActionsModal({ items, open, onClose, isAdmin, axiosAuth, onReload, currentUserRole }) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);  // { item, confirmText, reason }
  const [approvalWarning, setApprovalWarning] = useState(null);  // { item, preview }

  const isStaffRole = ["admin", "super_admin", "rrhh", "jefe_taller", "jefe_recambios"].includes(currentUserRole);

  const doAction = useCallback(async (item, action, payload = {}) => {
    setBusy(true);
    try {
      if (action === "cancel") {
        await axiosAuth().put(`/vacation-requests/${item.id}/cancel`, {});
        toast.success("Solicitud cancelada");
      } else {
        await axiosAuth().put(`/vacation-requests/${item.id}/approve`, { action, notes: payload.notes || "" });
        toast.success(`Acción "${action}" registrada`);
      }
      onReload();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Error en ${action}`);
    } finally { setBusy(false); }
  }, [axiosAuth, onClose, onReload]);

  const tryApprove = useCallback(async (item) => {
    // Alerta preventiva: consultar cobertura tras aprobar
    setBusy(true);
    try {
      const r = await axiosAuth().get(`/admin/coverage-approval-preview?request_id=${item.id}`);
      if (r.data && r.data.has_critical) {
        setApprovalWarning({ item, preview: r.data });
        setBusy(false);
        return;
      }
    } catch { /* si falla preview, proseguimos */ }
    setBusy(false);
    doAction(item, "approve");
  }, [axiosAuth, doAction]);

  const doDelete = useCallback(async () => {
    if (!confirmDelete) return;
    const { item, confirmText, reason } = confirmDelete;
    if (confirmText !== "ELIMINAR") { toast.error('Debes escribir "ELIMINAR" para confirmar'); return; }
    if (!reason || reason.trim().length < 10) { toast.error("Motivo obligatorio (mín. 10 caracteres)"); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams({ confirm: "ELIMINAR", reason: reason.trim() });
      await axiosAuth().delete(`/vacation-requests/${item.id}?${params}`);
      toast.success("Solicitud eliminada (registrado en auditoría)");
      setConfirmDelete(null);
      onReload();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    } finally { setBusy(false); }
  }, [confirmDelete, axiosAuth, onClose, onReload]);

  if (!items || items.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="actions-modal">
        <DialogHeader>
          <DialogTitle className="text-base uppercase">Detalle de ausencia</DialogTitle>
          <DialogDescription className="text-xs">{items.length} solicitud(es)</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {items.map((e) => {
            const canApproveReject = isStaffRole && (e.status === "pending");
            const canCancel = isStaffRole || e.status === "pending";
            const canClose = isStaffRole && e.status === "approved";
            const canDelete = ["admin", "super_admin"].includes(currentUserRole);
            return (
              <div key={e.id} className="border-l-4 rounded-sm bg-slate-50 p-3 text-xs" style={{ borderColor: e.color }}>
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <p className="font-semibold text-sm">{e.employee_name}</p>
                    <p className="text-[11px] text-slate-600">{e.type_label} · {e.start_date} → {e.end_date} · {e.working_days} laborables</p>
                  </div>
                  <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-sm" style={{ background: e.color, color: "#fff" }}>{e.status_label}</span>
                </div>
                {e.approver_name && <p className="text-slate-500 text-[10px]">Aprobado por {e.approver_name}{e.approved_at && ` · ${String(e.approved_at).slice(0,16)}`}</p>}
                {e.comment && <p className="italic text-slate-600 mt-1">Comentario: {e.comment}</p>}
                {e.rejection_reason && <p className="text-red-700 mt-1">Motivo: {e.rejection_reason}</p>}
                {/* Acciones */}
                {isAdmin && (
                  <div className="flex gap-1.5 flex-wrap mt-2 pt-2 border-t border-slate-200">
                    {canApproveReject && (
                      <>
                        <Button size="sm" disabled={busy} className="rounded-sm text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => tryApprove(e)} data-testid={`act-approve-${e.id}`}>
                          <Check className="w-3 h-3 mr-1" /> Aprobar
                        </Button>
                        <Button size="sm" disabled={busy} variant="outline" className="rounded-sm text-[11px] text-red-700 border-red-300" onClick={() => {
                          const reason = prompt("Motivo del rechazo:");
                          if (reason) doAction(e, "reject", { notes: reason });
                        }} data-testid={`act-reject-${e.id}`}>
                          <X className="w-3 h-3 mr-1" /> Rechazar
                        </Button>
                      </>
                    )}
                    {canCancel && e.status !== "cancelled" && e.status !== "rejected" && (
                      <Button size="sm" disabled={busy} variant="outline" className="rounded-sm text-[11px]" onClick={() => {
                        if (window.confirm(`¿Cancelar la solicitud de ${e.employee_name}?`)) doAction(e, "cancel");
                      }} data-testid={`act-cancel-${e.id}`}>
                        <Ban className="w-3 h-3 mr-1" /> Cancelar
                      </Button>
                    )}
                    {canClose && (
                      <Button size="sm" disabled={busy} variant="outline" className="rounded-sm text-[11px]" onClick={() => {
                        if (window.confirm(`¿Cerrar la solicitud de ${e.employee_name}?`)) doAction(e, "close");
                      }} data-testid={`act-close-${e.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Cerrar
                      </Button>
                    )}
                    {canDelete && (
                      <Button size="sm" disabled={busy} variant="outline" className="rounded-sm text-[11px] text-red-700 border-red-300 ml-auto"
                              onClick={() => setConfirmDelete({ item: e, confirmText: "", reason: "" })}
                              data-testid={`act-delete-${e.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Doble confirmación de eliminación */}
        {confirmDelete && (
          <div className="border-2 border-red-300 bg-red-50 rounded-sm p-3 mt-2 space-y-2" data-testid="delete-confirm-panel">
            <p className="text-xs text-red-900 font-semibold uppercase">⚠ Doble confirmación obligatoria</p>
            <p className="text-[11px] text-red-900">
              Vas a eliminar DEFINITIVAMENTE la solicitud de <strong>{confirmDelete.item.employee_name}</strong>
              ({confirmDelete.item.type_label}, {confirmDelete.item.start_date} → {confirmDelete.item.end_date}).
              Quedará registrado en auditoría con tu nombre, fecha y motivo.
            </p>
            <Input
              placeholder='Escribe "ELIMINAR" para confirmar'
              value={confirmDelete.confirmText}
              onChange={(e) => setConfirmDelete({ ...confirmDelete, confirmText: e.target.value })}
              className="rounded-sm h-8 text-xs"
              data-testid="delete-confirm-text"
            />
            <Input
              placeholder="Motivo de eliminación (mínimo 10 caracteres)"
              value={confirmDelete.reason}
              onChange={(e) => setConfirmDelete({ ...confirmDelete, reason: e.target.value })}
              className="rounded-sm h-8 text-xs"
              data-testid="delete-confirm-reason"
            />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmDelete(null)} className="rounded-sm text-xs">Cancelar</Button>
              <Button size="sm" disabled={busy || confirmDelete.confirmText !== "ELIMINAR" || confirmDelete.reason.trim().length < 10}
                      onClick={doDelete} className="rounded-sm text-xs bg-red-700 hover:bg-red-800" data-testid="delete-confirm-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Eliminar definitivamente
              </Button>
            </div>
          </div>
        )}

        {/* Alerta preventiva de cobertura */}
        {approvalWarning && (
          <div className="border-2 border-amber-300 bg-amber-50 rounded-sm p-3 mt-2 space-y-2" data-testid="approval-warning-panel">
            <p className="text-xs text-amber-900 font-semibold uppercase flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Atención: cobertura crítica
            </p>
            <p className="text-[11px] text-amber-900">
              Si apruebas esta solicitud de <strong>{approvalWarning.item.employee_name}</strong>, el grupo (
              <strong>{approvalWarning.preview.request.center || "Sin centro"}{approvalWarning.preview.request.department ? ` · ${approvalWarning.preview.request.department}` : ""}</strong>) quedará por debajo del umbral crítico en los siguientes días:
            </p>
            <ul className="text-[11px] space-y-0.5 max-h-32 overflow-y-auto">
              {approvalWarning.preview.warnings.slice(0, 10).map((w) => (
                <li key={w.date} className="px-2 py-0.5 bg-white border-l-4 border-red-400 rounded-sm font-mono">
                  {w.date}: <strong>{w.available} de {w.total}</strong> disponibles ({w.pct_available}%)
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-amber-800">Umbrales: verde ≥{approvalWarning.preview.thresholds.green_min_pct}% · ajustado ≥{approvalWarning.preview.thresholds.yellow_min_pct}% · crítico &lt;{approvalWarning.preview.thresholds.yellow_min_pct}%.</p>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setApprovalWarning(null)} className="rounded-sm text-xs" data-testid="warn-cancel">Cancelar</Button>
              <Button size="sm" disabled={busy} onClick={() => { const it = approvalWarning.item; setApprovalWarning(null); doAction(it, "approve"); }} className="rounded-sm text-xs bg-amber-600 hover:bg-amber-700" data-testid="warn-confirm-approve">
                Aprobar igualmente
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button size="sm" variant="outline" onClick={onClose} className="rounded-sm text-xs">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══ Modal Cobertura diaria (Iter cobertura) ═══
function CoverageDayModal({ cov, open, onClose }) {
  if (!cov) return null;
  const c = COVERAGE_COLORS[cov.status] || COVERAGE_COLORS.ok;
  const statusLabel = { ok: "OK", ajustado: "Ajustado", critico: "Crítico" }[cov.status] || cov.status;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="coverage-day-modal">
        <DialogHeader>
          <DialogTitle className="text-base uppercase">Cobertura · {cov.date}</DialogTitle>
          <DialogDescription className="text-xs">
            <span className="px-2 py-0.5 rounded-sm font-mono mr-2" style={{ background: c.bg, color: c.text }}>{statusLabel}</span>
            <strong className="font-mono">{cov.available} de {cov.total}</strong> disponibles ({cov.pct_available}%) · {cov.absent} ausentes
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
          <Card className="rounded-sm border border-slate-200 p-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Disponibles ({cov.available})</p>
            {cov.available_list.length === 0 ? (
              <p className="text-xs text-slate-400">Nadie disponible</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {cov.available_list.map((e) => (
                  <li key={e.id} className="border-l-2 border-emerald-500 pl-2 py-0.5">
                    <strong>{e.name}</strong>
                    {(e.center || e.department) && (
                      <span className="text-[10px] text-slate-400"> · {e.center}{e.center && e.department ? " · " : ""}{e.department}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card className="rounded-sm border border-slate-200 p-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Ausentes ({cov.absent})</p>
            {cov.absent_list.length === 0 ? (
              <p className="text-xs text-slate-400">Sin ausencias</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {cov.absent_list.map((a) => (
                  <li key={a.id} className="border-l-4 pl-2 py-0.5" style={{ borderColor: a.color }}>
                    <div className="font-semibold">{a.name}</div>
                    <div className="text-[10px] text-slate-500">
                      <span className="px-1 rounded-sm text-white" style={{ background: a.color }}>{a.type_label}</span>
                      <span className="ml-1">{a.status_label}</span>
                      <span className="ml-1 text-slate-400">· {a.start_date}→{a.end_date}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
        <DialogFooter>
          <Button size="sm" variant="outline" onClick={onClose} className="rounded-sm text-xs">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════
export default function AbsencesCalendarPage({ admin = false }) {
  const { axiosAuth, API, user } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 174: vista por defecto del admin = "team" (Planning mensual). Año queda como vista secundaria.
  const [view, setView] = useState(admin ? "dashboard" : "month");
  const [filters, setFilters] = useState({ employee: "", center: "", department: "", type: "", status: "" });
  const [detail, setDetail] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [reloadKey, setReloadKey] = useState(0);
  // Catalogos para filtros
  const [allEmployees, setAllEmployees] = useState([]);  // [{id, name, first_name, last_name, center, department}]
  const [centers, setCenters] = useState([]);            // [{id, name}]
  const [departments, setDepartments] = useState([]);    // [{id, name, center_id}]
  // Cobertura del mes
  const [coverage, setCoverage] = useState(null);
  const [coverageDay, setCoverageDay] = useState(null);
  // Día resaltado desde modal de cobertura (ISO YYYY-MM-DD)
  const [highlightDate, setHighlightDate] = useState(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Cargar catalogos una vez (solo admin)
  useEffect(() => {
    if (!admin) return;
    let alive = true;
    (async () => {
      try {
        const [empRes, centersRes, deptsRes] = await Promise.all([
          axiosAuth().get("/employees?archived=false"),
          axiosAuth().get("/hr-catalogs/centers"),
          axiosAuth().get("/hr-catalogs/departments"),
        ]);
        if (!alive) return;
        const emps = (empRes.data || []).filter((e) => e && e.id);
        setAllEmployees(emps);
        setCenters((centersRes.data || []).filter((c) => c.active !== false));
        setDepartments((deptsRes.data || []).filter((d) => d.active !== false));
      } catch (e) { /* silencio */ }
    })();
    return () => { alive = false; };
  }, [admin, axiosAuth]);

  // Cargar calendario (items)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ year });
        if (admin) Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
        const path = admin ? `/admin/absences-calendar?${params}` : `/me/absences-calendar?${params}`;
        const res = await axiosAuth().get(path);
        if (alive) setData(res.data);
      } catch (err) {
        if (alive) toast.error("Error cargando calendario");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [year, admin, JSON.stringify(filters), reloadKey]);

  // Cargar cobertura del mes para vista 'team'
  useEffect(() => {
    if (!admin || view !== "team") return;
    let alive = true;
    (async () => {
      try {
        const params = new URLSearchParams({ year, month: month + 1 });
        if (filters.center) params.set("center", filters.center);
        if (filters.department) params.set("department", filters.department);
        if (filters.employee) params.set("employee", filters.employee);
        const r = await axiosAuth().get(`/admin/coverage-daily?${params}`);
        if (alive) setCoverage(r.data);
      } catch (e) { /* sin coverage */ }
    })();
    return () => { alive = false; };
  }, [admin, view, year, month, JSON.stringify(filters), reloadKey, axiosAuth]);

  // Empleados disponibles para el selector (filtrados por centro/departamento)
  const employeeOptions = useMemo(() => {
    const filtered = allEmployees.filter((e) => {
      if (filters.center) {
        const c = (e.center || "").toLowerCase();
        if (!c.includes(filters.center.toLowerCase())) return false;
      }
      if (filters.department) {
        const d = (e.department || "").toLowerCase();
        if (!d.includes(filters.department.toLowerCase())) return false;
      }
      return true;
    });
    return filtered.map((e) => ({
      id: e.id,
      label: (e.first_name || e.last_name) ? `${e.first_name || ""} ${e.last_name || ""}`.trim() : (e.name || "—"),
      center: e.center || "",
      department: e.department || "",
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [allEmployees, filters.center, filters.department]);

  // Limpia empleado seleccionado si ya no pertenece al filtro (vía cambio de filtros, no efecto)
  const employeeStillValid = filters.employee
    ? employeeOptions.some((e) => e.id === filters.employee)
    : true;
  const effectiveFilters = employeeStillValid
    ? filters
    : { ...filters, employee: "" };

  // Opciones de centro / departamento (todos los activos)
  const centerOptions = useMemo(() => centers.map((c) => ({ value: c.name, label: c.name })).sort((a, b) => a.label.localeCompare(b.label)), [centers]);
  const departmentOptions = useMemo(() => {
    const all = departments.map((d) => ({ value: d.name, label: d.name, center_id: d.center_id }));
    if (!filters.center) return all.sort((a, b) => a.label.localeCompare(b.label));
    const selectedCenter = centers.find((c) => c.name === filters.center);
    if (!selectedCenter) return all.sort((a, b) => a.label.localeCompare(b.label));
    return all.filter((d) => !d.center_id || d.center_id === selectedCenter.id).sort((a, b) => a.label.localeCompare(b.label));
  }, [departments, centers, filters.center]);

  const exportExcel = async () => {
    const params = new URLSearchParams({ year });
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    const r = await fetch(`${API}/admin/absences-calendar.xlsx?${params}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await r.blob()); a.download = `ausencias_${year}.xlsx`; a.click();
  };

  if (loading || !data) return <div className="p-8 text-xs text-slate-500" data-testid="abs-loading">Cargando…</div>;

  return (
    <div className="space-y-3" data-testid={admin ? "absences-admin-page" : "absences-me-page"}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-industrial text-2xl uppercase tracking-wide">
            <CalendarIcon className="w-5 h-5 inline mr-2 mb-1" />
            {admin ? "Planificación de ausencias" : "Mi calendario"}
          </h1>
          <p className="text-xs text-slate-500 mt-1">{data.items.length} solicitudes en {year}{data.overlaps?.length ? ` · ${data.overlaps.length} solape(s)` : ""}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {admin ? (
            <>
              <Button size="sm" variant={view === "dashboard" ? "default" : "outline"} onClick={() => setView("dashboard")} className="rounded-sm text-xs" data-testid="view-dashboard"><LayoutDashboard className="w-3 h-3 mr-1" /> Dashboard</Button>
              <Button size="sm" variant={view === "team" ? "default" : "outline"} onClick={() => setView("team")} className="rounded-sm text-xs" data-testid="view-team"><Users className="w-3 h-3 mr-1" /> Planning equipo</Button>
              <Button size="sm" variant={view === "list" ? "default" : "outline"} onClick={() => setView("list")} className="rounded-sm text-xs" data-testid="view-list"><List className="w-3 h-3 mr-1" /> Lista</Button>
              <Button size="sm" variant={view === "year" ? "default" : "outline"} onClick={() => setView("year")} className="rounded-sm text-xs" data-testid="view-year"><Grid3x3 className="w-3 h-3 mr-1" /> Año (consulta)</Button>
              <Button size="sm" onClick={exportExcel} className="rounded-sm text-xs uppercase" data-testid="export-xlsx"><Download className="w-3 h-3 mr-1" /> Excel</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant={view === "month" ? "default" : "outline"} onClick={() => setView("month")} className="rounded-sm text-xs" data-testid="view-month">Mes</Button>
              <Button size="sm" variant={view === "year" ? "default" : "outline"} onClick={() => setView("year")} className="rounded-sm text-xs" data-testid="view-year">Año</Button>
            </>
          )}
        </div>
      </div>

      {/* Navegación mes */}
      {(view === "team" || view === "month") && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); }} className="rounded-sm text-xs">‹</Button>
          <span className="text-xs font-semibold uppercase tracking-wider px-2" data-testid="current-month">{MONTHS[month]} {year}</span>
          <Button size="sm" variant="outline" onClick={() => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); }} className="rounded-sm text-xs">›</Button>
          <Button size="sm" variant="ghost" onClick={() => { const n = new Date(); setYear(n.getFullYear()); setMonth(n.getMonth()); }} className="rounded-sm text-xs">Hoy</Button>
        </div>
      )}

      {/* Badge "Día seleccionado" cuando venimos del modal de cobertura */}
      {view === "team" && highlightDate && (() => {
        const [hy, hm, hd] = highlightDate.split("-").map(Number);
        const inMonth = hy === year && hm === month + 1;
        if (!inMonth) return null;
        return (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border-l-4 border-blue-500 rounded-sm text-xs" data-testid="day-selected-badge">
            <CalendarIcon className="w-3.5 h-3.5 text-blue-700" />
            <span className="text-blue-900">Día seleccionado: <strong className="font-mono">{String(hd).padStart(2, "0")}/{String(hm).padStart(2, "0")}/{hy}</strong></span>
            <button onClick={() => setHighlightDate(null)} className="ml-2 text-blue-700 hover:text-blue-900 text-[10px] uppercase tracking-wider" data-testid="clear-day-selected">Quitar</button>
          </div>
        );
      })()}
      {view === "year" && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setYear(year - 1)} className="rounded-sm text-xs">‹ {year - 1}</Button>
          <span className="text-xs font-semibold px-2">{year}</span>
          <Button size="sm" variant="outline" onClick={() => setYear(year + 1)} className="rounded-sm text-xs">{year + 1} ›</Button>
        </div>
      )}

      {/* Summary /me */}
      {data.summary && (
        <Card className="rounded-sm p-3 border border-slate-200">
          {!data.summary.coherent && (
            <div className="bg-red-50 border border-red-200 text-red-900 text-xs rounded-sm p-2 mb-2">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {data.summary.coherence_msg}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
            <div><p className="text-[10px] uppercase text-slate-500">Asignados</p><p className="text-2xl font-mono font-bold">{data.summary.assigned}</p></div>
            <div><p className="text-[10px] uppercase text-slate-500">Disponibles</p><p className="text-2xl font-mono font-bold text-slate-900" data-testid="sum-available">{data.summary.available}</p></div>
            <div><p className="text-[10px] uppercase text-slate-500">Aprobados futuros</p><p className="text-2xl font-mono font-bold text-emerald-600">{data.summary.approved_future}</p></div>
            <div><p className="text-[10px] uppercase text-slate-500">Disfrutados</p><p className="text-2xl font-mono font-bold text-blue-600">{data.summary.used_past}</p></div>
            <div><p className="text-[10px] uppercase text-slate-500">Pendientes</p><p className="text-2xl font-mono font-bold text-amber-500">{data.summary.pending}</p></div>
          </div>
        </Card>
      )}

      {/* Filtros admin (no para dashboard) */}
      {admin && view !== "dashboard" && (
        <Card className="rounded-sm p-2 border border-slate-200">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <SearchableEmployeeSelect
              value={effectiveFilters.employee}
              onChange={(v) => setFilters({ ...filters, employee: v })}
              employees={employeeOptions}
              testid="f-emp"
            />
            <NativeSelect
              value={filters.center}
              onChange={(v) => setFilters({ ...filters, center: v, department: "", employee: "" })}
              options={centerOptions}
              placeholder="Todos los centros"
              testid="f-center"
            />
            <NativeSelect
              value={filters.department}
              onChange={(v) => setFilters({ ...filters, department: v, employee: "" })}
              options={departmentOptions}
              placeholder="Todos los departamentos"
              testid="f-dept"
            />
            <select value={filters.type} onChange={(e) => setFilters({...filters, type: e.target.value})} className="rounded-sm h-8 text-xs border border-slate-300 px-2 bg-white" data-testid="f-type"><option value="">Todos los tipos</option><option value="vacaciones">Vacaciones</option><option value="baja_medica">Baja</option><option value="permiso_retribuido">Permiso retrib.</option><option value="asuntos_propios">Asuntos propios</option></select>
            <select value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})} className="rounded-sm h-8 text-xs border border-slate-300 px-2 bg-white" data-testid="f-status"><option value="">Todos los estados</option><option value="approved">Aprobadas</option><option value="pending">Pendientes</option><option value="rejected">Rechazadas</option><option value="cancelled">Canceladas</option></select>
            {(filters.employee || filters.center || filters.department || filters.type || filters.status) && (
              <Button size="sm" variant="ghost" className="rounded-sm text-xs h-8" onClick={() => setFilters({ employee: "", center: "", department: "", type: "", status: "" })} data-testid="f-clear">
                Limpiar filtros
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Leyenda (no para dashboard) */}
      {view !== "dashboard" && (
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          {LEGEND.map((l) => (
            <span key={l.label} className={`inline-flex items-center gap-1 px-1.5 py-0.5 border border-slate-200 rounded-sm ${l.muted ? "opacity-60" : ""}`}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: l.color }} />{l.label}
            </span>
          ))}
        </div>
      )}

      {/* Render por vista */}
      {admin && view === "dashboard" && <HrAbsencesDashboard
        axiosAuth={axiosAuth}
        onClickRequest={(evs) => setDetail({ evs })}
        onOpenPlanning={({ scope, name }) => {
          // Mantener el mes actual (dashboard refleja hoy → mes corriente)
          const today = new Date();
          setYear(today.getFullYear());
          setMonth(today.getMonth());
          setHighlightDate(today.toISOString().slice(0, 10));
          // Aplicar filtro según scope. "Sin centro" / "Sin departamento" se traducen a vacío.
          const isPlaceholder = name === "Sin centro" || name === "Sin departamento";
          if (scope === "centro") {
            setFilters({ employee: "", center: isPlaceholder ? "" : name, department: "", type: "", status: "" });
          } else {
            setFilters({ employee: "", center: "", department: isPlaceholder ? "" : name, type: "", status: "" });
          }
          setView("team");
        }}
      />}
      {view === "team" && <TeamMonthlyPlanning year={year} month={month} items={data.items} onClickCell={(evs) => setDetail({ evs })} sortBy={sortBy} setSortBy={setSortBy} coverageDays={coverage?.days} onClickCoverage={(c) => setCoverageDay(c)} highlightDate={highlightDate} />}
      {view === "list" && <ListView items={data.items} onClickItem={(evs) => setDetail({ evs })} />}
      {view === "month" && <EmployeeMonthlyView year={year} month={month} items={data.items} onClickDay={(evs) => setDetail({ evs })} />}
      {view === "year" && <YearDots year={year} items={data.items} onClickDay={(evs) => setDetail({ evs })} />}

      {/* Próximas ausencias en empleado */}
      {!admin && (view === "month" || view === "year") && (
        <Card className="rounded-sm p-3 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Próximas ausencias</p>
          {data.items.filter(it => it.end_date >= todayIso()).slice(0,5).map((it) => (
            <button key={it.id} onClick={() => setDetail({ evs: [it] })} className="block w-full text-left text-xs border-l-4 bg-slate-50 p-2 rounded-sm mb-1.5" style={{ borderColor: it.color }}>
              <span className="font-semibold">{it.type_label}</span> · {it.start_date} → {it.end_date} · <span className="text-slate-500">{it.status_label}</span>
            </button>
          )) || <p className="text-xs text-slate-400">Sin ausencias próximas.</p>}
        </Card>
      )}

      <ActionsModal
        items={detail?.evs}
        open={!!detail}
        onClose={() => setDetail(null)}
        isAdmin={admin}
        axiosAuth={axiosAuth}
        onReload={reload}
        currentUserRole={user?.role || ""}
      />

      <CoverageDayModal cov={coverageDay} open={!!coverageDay} onClose={() => setCoverageDay(null)} />
    </div>
  );
}
